import { Fragment } from 'react';
import { ArrowLeft } from 'lucide-react';
import { usePreferences } from '../app/preferences.ts';
import { Link } from '../components/Link.tsx';
import { CONTACT_EMAIL, INFO_PAGES, INFO_PATHS, type InfoPageId } from '../content/infoPages.ts';

/** The chess guide, privacy policy and about page: plain articles. */
export function InfoPage({ id }: { id: InfoPageId }) {
  const { prefs, t } = usePreferences();
  const page = INFO_PAGES[id][prefs.locale];

  return (
    <article className="info-page">
      <div className="page-top">
        <Link href="/" className="back-link">
          <ArrowLeft size={22} strokeWidth={2.5} /> {t('nav.home')}
        </Link>
      </div>
      <header className="info-head">
        <h1 className="page-title">
          <span className="emoji" aria-hidden="true">
            {page.emoji}
          </span>{' '}
          {page.title}
        </h1>
        {page.note && <p className="info-note">{page.note}</p>}
        <p className="info-intro">{page.intro}</p>
      </header>
      {page.sections.map((section) => (
        <section key={section.heading} className="info-section card">
          <h2>{section.heading}</h2>
          {section.paragraphs?.slice(0, section.list ? 1 : undefined).map((p) => <Paragraph key={p} text={p} />)}
          {section.list && (
            <ul>
              {section.list.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          )}
          {section.list && section.paragraphs?.slice(1).map((p) => <Paragraph key={p} text={p} />)}
        </section>
      ))}
      <InfoLinks current={id} />
    </article>
  );
}

/** A paragraph with "{email}" turned into a mail link. */
function Paragraph({ text }: { text: string }) {
  const parts = text.split('{email}');
  return (
    <p>
      {parts.map((part, i) => (
        <Fragment key={i}>
          {part}
          {i < parts.length - 1 && <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>}
        </Fragment>
      ))}
    </p>
  );
}

/** Links to the three articles; shown on the home page and under each article. */
export function InfoLinks({ current }: { current?: InfoPageId }) {
  const { t } = usePreferences();
  const links: { id: InfoPageId; label: 'info.guide' | 'info.about' | 'info.privacy' }[] = [
    { id: 'guide', label: 'info.guide' },
    { id: 'about', label: 'info.about' },
    { id: 'privacy', label: 'info.privacy' },
  ];
  return (
    <nav className="info-links" aria-label={t('info.nav')}>
      {links
        .filter((l) => l.id !== current)
        .map((l) => (
          <Link key={l.id} href={INFO_PATHS[l.id]}>
            {t(l.label)}
          </Link>
        ))}
    </nav>
  );
}
